/**
 * GP-1146: shared formatting for the "User" column of audit timelines.
 *
 * The backend records either a real user (from the JWT via the request-actor
 * context), a named automation trigger (`system:<module>.<function>` id /
 * "PHOX Automation — <trigger>" display name), or — on legacy rows only — a
 * bare 'system'. Named triggers must surface the exact function that ran;
 * only legacy rows fall back to the anonymous "PHOX (Automated)" label.
 *
 * GP-1144: the timeline synthesizes a few events from order fields (they are
 * not action-log rows and structurally carry no actor): "Descartes Shipment
 * Created" (metadata.descartesCreatedAt) and "Initial SMS Sent"
 * (smsLastSentAt). Rendering those as "Unknown" made automated system activity
 * read as if a person did something mysterious. When the actor is blank AND
 * the event type is one of those machine-generated ones, attribute it to
 * "PHOX (Automated)".
 *
 * The synthesized "Patient SMS Confirmation" event (smsConfirmedAt) is
 * attributed to "Patient (via SMS)" — the patient performed it, so neither
 * "Unknown" nor "PHOX (Automated)" is truthful.
 *
 * Deliberately NOT keyed on event category: backend categories are not a
 * human/machine signal — 'exception' covers human cancels & troubleshooting,
 * 'integration' covers HITL (human-in-the-loop), 'communication' covers
 * staff-logged calls. A blank actor on any other event type stays "Unknown"
 * (GP-1146 rule: never invent provenance for a row whose actor was lost).
 */
export interface ActorDisplay {
  label: string;
  automated: boolean;
  unknown: boolean;
}

const AUTOMATED_LABEL = 'PHOX (Automated)';

// Timeline events the frontend synthesizes from order fields (see
// transformToTimelineEvents in orders.service.ts). These are always produced
// by the automated pipeline and never carry an actor, so a blank actor on one
// of them IS the automation — not a person whose attribution was lost.
const AUTOMATED_EVENT_TYPES = new Set([
  'sms_sent', // synthesized from order.smsLastSentAt; real sms rows log 'System'
  'descartes_shipment_created', // synthesized from metadata.descartesCreatedAt
]);

// Synthesized from order.smsConfirmedAt ("Patient confirmed via SMS"): the
// actor is definitionally the PATIENT, not PHOX and not an unknown staffer.
const PATIENT_SMS_EVENT_TYPE = 'sms_confirmed';
const PATIENT_SMS_LABEL = 'Patient (via SMS)';

export function formatActorDisplay(
  performedByName?: string | null,
  eventType?: string | null,
): ActorDisplay {
  const name = (performedByName || '').trim();

  if (!name) {
    if (eventType && AUTOMATED_EVENT_TYPES.has(eventType)) {
      return { label: AUTOMATED_LABEL, automated: true, unknown: false };
    }
    if (eventType === PATIENT_SMS_EVENT_TYPE) {
      return { label: PATIENT_SMS_LABEL, automated: false, unknown: false };
    }
    return { label: 'Unknown', automated: false, unknown: true };
  }
  if (name === 'system' || name === 'System') {
    return { label: AUTOMATED_LABEL, automated: true, unknown: false };
  }
  if (name.startsWith('system:')) {
    return {
      label: `${AUTOMATED_LABEL} — ${name.slice('system:'.length)}`,
      automated: true,
      unknown: false,
    };
  }
  if (name.startsWith('PHOX Automation')) {
    const trigger = name.replace(/^PHOX Automation\s*—?\s*/, '');
    return {
      label: trigger ? `${AUTOMATED_LABEL} — ${trigger}` : AUTOMATED_LABEL,
      automated: true,
      unknown: false,
    };
  }
  return { label: name, automated: false, unknown: false };
}
